import React, { createContext, useState, useContext, useEffect } from 'react';
import { setAuthToken, logoutApi, getMe } from '../services/api';
import { isSuperAdminUser, isAdminOrSuperUser } from '../utils/userRoles';
import {
  getAuthTokenFromStorage,
  getAuthUserRawFromStorage,
  clearAuthPersistence,
  persistAuthSession,
  persistUserCache,
} from '../utils/authStorage';
import { normalizeAuthUser } from '../utils/planFeatures';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = getAuthTokenFromStorage();
    const userEntry = getAuthUserRawFromStorage();

    if (!token) {
      setIsLoading(false);
      return undefined;
    }

    setAuthToken(token);
    if (userEntry?.raw) {
      try {
        const parsed = normalizeAuthUser(JSON.parse(userEntry.raw));
        setUser(parsed);
        setIsAuthenticated(true);
      } catch (_) {
        /* ignore */
      }
    }

    getMe()
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res?.user) {
          const freshUser = normalizeAuthUser(res.user);
          setUser(freshUser);
          persistUserCache(freshUser);
          setIsAuthenticated(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          clearAuthPersistence();
          setAuthToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = (token, userData) => {
    const normalized = normalizeAuthUser(userData);
    persistAuthSession(token, normalized);
    setAuthToken(String(token).trim());
    setUser(normalized);
    setIsAuthenticated(true);
  };

  const logout = () => {
    const token = getAuthTokenFromStorage();
    logoutApi(token);
    setAuthToken(null);
    clearAuthPersistence();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    const normalized = normalizeAuthUser(userData);
    setUser(normalized);
    persistUserCache(normalized);
  };

  const refreshUser = () => {
    return getMe()
      .then((res) => {
        if (res?.success && res?.user) {
          const freshUser = normalizeAuthUser(res.user);
          setUser(freshUser);
          persistUserCache(freshUser);
          return freshUser;
        }
        return null;
      })
      .catch(() => null);
  };

  const isSuperAdmin = isSuperAdminUser(user);
  const isAdmin = isAdminOrSuperUser(user);

  const getToken = () => getAuthTokenFromStorage();

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        updateUser,
        refreshUser,
        getToken,
        isSuperAdmin,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
